import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  getDocs,
  onSnapshot,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import {
  ProposalMilestone,
  MilestoneStatus,
  MilestonePresets,
  Opportunity,
  KeyDate,
} from "../types";
import { computeAutoMilestones } from "../utils/calendarUtils";

/**
 * Subscribes to the milestones subcollection of an opportunity.
 */
export function subscribeToMilestones(
  opportunityId: string,
  callback: (milestones: ProposalMilestone[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const colRef = collection(db, "opportunities", opportunityId, "milestones");
  const q = query(colRef, orderBy("targetDate", "asc"));

  return onSnapshot(
    q,
    (snapshot) => {
      const items: ProposalMilestone[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          opportunityId,
          title: String(data.title || ""),
          description: data.description ? String(data.description) : undefined,
          targetDate: String(data.targetDate || ""),
          targetTime: data.targetTime ? String(data.targetTime) : undefined,
          timezone: String(data.timezone || "America/Chicago"),
          category: data.category || "internal_gate",
          status: (data.status as MilestoneStatus) || "pending",
          autoKey: data.autoKey || undefined,
          humanEdited: Boolean(data.humanEdited),
          compressed: Boolean(data.compressed),
          ownerEmail: data.ownerEmail ? String(data.ownerEmail) : undefined,
          ownerName: data.ownerName ? String(data.ownerName) : undefined,
          sourcePage: data.sourcePage ?? undefined,
          completedAt: data.completedAt ? String(data.completedAt) : undefined,
          completedByEmail: data.completedByEmail ? String(data.completedByEmail) : undefined,
          createdAt: String(data.createdAt || new Date().toISOString()),
          updatedAt: String(data.updatedAt || new Date().toISOString()),
        };
      });

      // Secondary sort in memory by targetTime
      items.sort((a, b) => {
        const cmp = a.targetDate.localeCompare(b.targetDate);
        if (cmp !== 0) return cmp;
        return (a.targetTime || "00:00").localeCompare(b.targetTime || "00:00");
      });

      callback(items);
    },
    (error) => {
      console.error("Error subscribing to milestones:", error);
      if (onError) onError(error);
    },
  );
}

/**
 * Creates a new manual proposal milestone.
 */
export async function createMilestone(
  opportunityId: string,
  milestone: Omit<ProposalMilestone, "id" | "opportunityId" | "createdAt" | "updatedAt">,
): Promise<string> {
  const now = new Date().toISOString();
  const colRef = collection(db, "opportunities", opportunityId, "milestones");

  const docRef = await addDoc(colRef, {
    ...milestone,
    humanEdited: true,
    createdAt: now,
    updatedAt: now,
  });

  return docRef.id;
}

/**
 * Updates an existing milestone. Sets humanEdited to true if dates or title are modified.
 */
export async function updateMilestone(
  opportunityId: string,
  milestoneId: string,
  updates: Partial<ProposalMilestone>,
): Promise<void> {
  const docRef = doc(db, "opportunities", opportunityId, "milestones", milestoneId);
  const now = new Date().toISOString();

  const isScheduleChange =
    updates.targetDate !== undefined ||
    updates.targetTime !== undefined ||
    updates.title !== undefined;

  const payload: Record<string, unknown> = {
    ...updates,
    updatedAt: now,
  };

  if (isScheduleChange) {
    payload.humanEdited = true;
  }

  await updateDoc(docRef, payload);
}

/**
 * Deletes a milestone.
 */
export async function deleteMilestone(opportunityId: string, milestoneId: string): Promise<void> {
  const docRef = doc(db, "opportunities", opportunityId, "milestones", milestoneId);
  await deleteDoc(docRef);
}

/**
 * Toggles a milestone between pending and completed.
 */
export async function toggleMilestoneCompleted(
  opportunityId: string,
  milestoneId: string,
  currentStatus: MilestoneStatus,
  userEmail?: string,
): Promise<void> {
  const docRef = doc(db, "opportunities", opportunityId, "milestones", milestoneId);
  const now = new Date().toISOString();

  if (currentStatus === "pending") {
    await updateDoc(docRef, {
      status: "completed",
      completedAt: now,
      completedByEmail: userEmail || "team",
      updatedAt: now,
    });
  } else {
    await updateDoc(docRef, {
      status: "pending",
      completedAt: null,
      completedByEmail: null,
      updatedAt: now,
    });
  }
}

/**
 * Upserts backward-scheduled auto milestones idempotently.
 * Preserves humanEdited and completed milestones.
 */
export async function upsertAutoMilestones(
  opportunity: Opportunity,
  presets?: Partial<MilestonePresets>,
): Promise<void> {
  const colRef = collection(db, "opportunities", opportunity.id, "milestones");
  const existingSnap = await getDocs(colRef);
  const existingMap = new Map<string, ProposalMilestone>();

  existingSnap.docs.forEach((d) => {
    existingMap.set(d.id, d.data() as ProposalMilestone);
  });

  const computed = computeAutoMilestones(opportunity, presets);

  for (const m of computed) {
    const existing = existingMap.get(m.id);
    if (existing) {
      // If user edited date or already completed, do not overwrite schedule!
      if (existing.humanEdited || existing.status === "completed") {
        continue;
      }
    }

    const docRef = doc(db, "opportunities", opportunity.id, "milestones", m.id);
    await setDoc(docRef, m, { merge: true });
  }
}

/**
 * Imports extracted RFP key dates as milestones into the schedule.
 */
export async function importExtractedDates(
  opportunity: Opportunity,
  extractedDates: KeyDate[],
): Promise<number> {
  if (!extractedDates || extractedDates.length === 0) return 0;

  const colRef = collection(db, "opportunities", opportunity.id, "milestones");
  const existingSnap = await getDocs(colRef);
  const existingTitles = new Set(existingSnap.docs.map((d) => d.data().title?.toLowerCase()));

  let importedCount = 0;
  const now = new Date().toISOString();

  for (const dateItem of extractedDates) {
    if (existingTitles.has(dateItem.label.toLowerCase())) {
      continue;
    }

    const category =
      dateItem.kind === "proposal_due" || dateItem.kind === "questions_due"
        ? "external_deadline"
        : dateItem.kind === "pre_bid"
          ? "client_event"
          : "custom";

    await addDoc(colRef, {
      title: dateItem.label,
      targetDate: dateItem.date,
      targetTime: dateItem.time || undefined,
      timezone: "America/Chicago",
      category,
      status: "pending",
      humanEdited: false,
      sourcePage: dateItem.sourcePage,
      createdAt: now,
      updatedAt: now,
    });

    importedCount++;
  }

  return importedCount;
}
